import { useEffect } from 'react';

/**
 * Robust patch for react-beautiful-dnd compatibility with React 18
 * This solution focuses on ensuring proper direct DOM manipulation
 * to guarantee that drag and drop works regardless of other React features
 */
export const useDndPatch = () => {
  useEffect(() => {
    // Create a simple flag to identify if this is the first mount in StrictMode
    // This helps avoid double-initialization issues in React 18 StrictMode
    if (!window.__REACT_BEAUTIFUL_DND_STRICT_MODE_PATCH) {
      window.__REACT_BEAUTIFUL_DND_STRICT_MODE_PATCH = true;
    } else {
      console.log('React Beautiful DnD patch already applied');
    }

    // Disable console warnings from react-beautiful-dnd
    const originalConsoleWarn = console.warn;
    const originalConsoleError = console.error;
    
    console.warn = (...args) => {
      if (
        typeof args[0] === 'string' && 
        (args[0].includes('react-beautiful-dnd') || 
         args[0].includes('attachEvent') ||
         args[0].includes('Strict mode'))
      ) {
        return; // Ignore warnings from react-beautiful-dnd
      }
      originalConsoleWarn.apply(console, args);
    };
    
    console.error = (...args) => {
      if (
        typeof args[0] === 'string' && 
        (args[0].includes('defaultProps') || 
         args[0].includes('forwardRef') ||
         args[0].includes('findDOMNode'))
      ) {
        return; // Ignore known React strict mode warnings
      }
      originalConsoleError.apply(console, args);
    };

    // Set up direct DOM event listeners to help react-beautiful-dnd
    const monitorDragableElements = () => {
      // Find all draggable elements and ensure they have proper attributes
      setTimeout(() => {
        const draggables = document.querySelectorAll('[data-rbd-draggable-id]');
        draggables.forEach(el => {
          // Make sure draggable elements can be interacted with
          el.style.userSelect = 'none';
          el.style.WebkitUserSelect = 'none';
          el.style.pointerEvents = 'auto';
          
          // Find all drag handles and make them super obvious
          const handles = el.querySelectorAll('[data-rbd-drag-handle-draggable-id]');
          handles.forEach(handle => {
            handle.style.cursor = 'grab';
            handle.style.pointerEvents = 'auto';
            handle.style.touchAction = 'manipulation';
            
            // Make it visually clear that this is draggable
            if (!handle.dataset.patchedDragHandle) {
              handle.dataset.patchedDragHandle = 'true';
              handle.style.transition = 'background-color 0.2s ease';
              
              // Add hover effect
              handle.addEventListener('mouseenter', () => {
                handle.style.backgroundColor = '#1565c0';
              });
              
              handle.addEventListener('mouseleave', () => {
                handle.style.backgroundColor = '#1976d2';
              });
            }
          });
        });
      }, 100);
    };

    // Call this initially
    monitorDragableElements();
    
    // Set up a mutation observer to catch dynamically added draggable elements
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && 
            (document.querySelector('[data-rbd-draggable-id]') || 
             document.querySelector('[data-rbd-drag-handle-draggable-id]'))) {
          monitorDragableElements();
          break;
        }
      }
    });
    
    // Start observing the entire document for changes
    observer.observe(document.body, { 
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-rbd-draggable-id', 'data-rbd-drag-handle-draggable-id']
    });

    return () => {
      // Clean up
      console.warn = originalConsoleWarn;
      console.error = originalConsoleError;
      observer.disconnect();
    };
  }, []);
};

// React Beautiful DND patch for React 18 compatibility
const logError = (error) => {
  // Only log actual errors, not 404s or expected scenarios
  if (error?.response?.status === 404) {
    console.warn('API endpoint not found:', error.message);
  } else if (error?.message?.includes('friends')) {
    console.warn('Friends feature not available:', error.message);
  } else {
    console.error(error);
  }
}; 